
import { useState } from "react";
import { useTranslations } from "use-intl";
import { Filter, FilterX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Filters, InvoiceStatus } from "@/types/payments";
import { cn } from "@/lib/utils";

// Import our filter components
import StatusFilter from "./filters/StatusFilter";
import AmountRangeFilter from "./filters/AmountRangeFilter";
import CurrencyFilter from "./filters/CurrencyFilter";
import DateRangeFilter from "./filters/DateRangeFilter";
import FilterActions from "./filters/FilterActions";

interface PaymentFiltersProps {
  filters: Filters;
  handleFilterChange: (key: keyof Filters, value: any) => void;
  handleStatusFilterChange?: (status: InvoiceStatus | 'all') => void;
  resetFilters: () => void;
  uniqueCurrencies: string[];
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  applyFilters: () => void;
  hasActiveFilters?: boolean;
}

const PaymentFilters = ({
  filters,
  handleFilterChange,
  handleStatusFilterChange,
  resetFilters,
  uniqueCurrencies,
  showFilters,
  setShowFilters,
  applyFilters,
  hasActiveFilters,
}: PaymentFiltersProps) => {
  const t = useTranslations("consultantPayables.filters");
  // Function to apply filters and hide the popover
  const handleApplyFilters = () => {
    applyFilters();
    setShowFilters(false);
  };
  
  // Handle currency checkbox change
  const handleCurrencyChange = (currency: string, checked: boolean) => {
    let updatedCurrencies: string[] | 'all' = filters.currency === 'all' ? [] : 
      Array.isArray(filters.currency) ? [...filters.currency] : [filters.currency];
    
    if (checked) {
      if (!Array.isArray(updatedCurrencies)) {
        updatedCurrencies = [currency];
      } else {
        updatedCurrencies.push(currency);
      }
    } else {
      if (Array.isArray(updatedCurrencies)) {
        updatedCurrencies = updatedCurrencies.filter(c => c !== currency);
        if (updatedCurrencies.length === 0) {
          updatedCurrencies = 'all';
        }
      }
    }
    
    handleFilterChange('currency', updatedCurrencies);
  };

  // Function to handle selecting all currencies
  const handleSelectAllCurrencies = () => {
    handleFilterChange('currency', 'all');
  };

  // Convert currency filter to expected format for CurrencyFilter component
  const getCurrencyForFilter = (): string[] | 'all' => {
    if (filters.currency === 'all') {
      return 'all';
    }
    if (Array.isArray(filters.currency)) {
      return filters.currency;
    }
    // Convert single string to array
    return [filters.currency];
  };
  
  return (
    <div className="flex items-center gap-2">
      <Popover open={showFilters} onOpenChange={setShowFilters}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              hasActiveFilters && "border-blue-400 bg-blue-50 text-blue-700 border"
            )}
          >
            {showFilters ? 
              <><FilterX className="mr-2 h-4 w-4" /> {t("hide")}</> : 
              <><Filter className="mr-2 h-4 w-4" /> {t("show")}</>
            }
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-auto min-w-[700px] p-4 bg-background z-50" align="start">
          <div className="grid grid-cols-3 gap-4">
            {/* Status Filter */}
            <StatusFilter 
              status={filters.status}
              onStatusChange={(value) => handleStatusFilterChange ? 
                handleStatusFilterChange(value) : 
                handleFilterChange('status', value)
              }
            />
            
            {/* Amount Range Filter */}
            <AmountRangeFilter 
              minAmount={filters.minAmount}
              maxAmount={filters.maxAmount}
              onMinAmountChange={(value) => handleFilterChange('minAmount', value)}
              onMaxAmountChange={(value) => handleFilterChange('maxAmount', value)}
            />
            
            {/* Currency Filter */}
            <CurrencyFilter 
              currencies={uniqueCurrencies}
              selectedCurrencies={getCurrencyForFilter()}
              onCurrencyChange={handleCurrencyChange}
              onSelectAllCurrencies={handleSelectAllCurrencies}
            />
          </div>

          {/* Date Range Filters */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Due Date Range */}
            <DateRangeFilter 
              title={t("dueRange")}
              fromDate={filters.dueDateFrom}
              toDate={filters.dueDateTo}
              onFromDateChange={(value) => handleFilterChange('dueDateFrom', value)}
              onToDateChange={(value) => handleFilterChange('dueDateTo', value)}
            />
            
            {/* Payment Date Range */}
            <DateRangeFilter 
              title={t("paymentRange")}
              fromDate={filters.paymentDateFrom}
              toDate={filters.paymentDateTo}
              onFromDateChange={(value) => handleFilterChange('paymentDateFrom', value)}
              onToDateChange={(value) => handleFilterChange('paymentDateTo', value)}
            />
          </div>
          
          {/* Filter Actions */}
          <FilterActions 
            onApply={handleApplyFilters}
            onReset={resetFilters}
          />
        </PopoverContent>
      </Popover>

      {/* Show Reset Filters button outside the popover when filters are active */}
      {hasActiveFilters && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={resetFilters}
          className="text-blue-700"
        >
          <FilterX className="mr-2 h-4 w-4" />
          {t("reset")}
        </Button>
      )}
    </div>
  );
};

export default PaymentFilters;
